class StickyATC extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
  }

  connectedCallback() {
    this.setupScrollBehavior();
    this.setupProductSync();
    this.setupDesktopRadios();
    this.setupDesktopFrequency();
    this.setupMobileDropdown();
  }

  disconnectedCallback() {
    if (this._outsideClick) {
      document.removeEventListener("click", this._outsideClick);
    }
  }

  /* ── Shared helpers ─────────────────────────────── */

  getMainProductElement() {
    return document.querySelector("main-product");
  }

  htmlToElement(html) {
    const template = document.createElement("template");
    template.innerHTML = html.trim();
    return template.content.firstElementChild;
  }

  updateSellingPlan(planId) {
    this.querySelectorAll("[data-sticky-plan]").forEach((input) => {
      input.value = planId || "";
    });
  }

  updateDisplayPrice(price) {
    const desktop = this.querySelector("[data-sticky-price-display]");
    const mobile = this.querySelector("[data-sticky-price-mobile]");
    if (desktop) desktop.textContent = price;
    if (mobile) mobile.textContent = price;
  }

  closeAllDropdowns() {
    const freqOptions = this.querySelector("[data-sticky-freq-options]");
    if (freqOptions) freqOptions.classList.add("hidden");
    const freqArrow = this.querySelector("[data-sticky-freq-arrow]");
    if (freqArrow) freqArrow.style.transform = "";

    const mobileOptions = this.querySelector("[data-sticky-mobile-options]");
    if (mobileOptions) mobileOptions.classList.add("hidden");
    const mobileArrow = this.querySelector("[data-sticky-mobile-arrow]");
    if (mobileArrow) mobileArrow.style.transform = "";
    const mobileToggle = this.querySelector("[data-sticky-mobile-toggle]");
    if (mobileToggle) mobileToggle.setAttribute("aria-expanded", "false");
  }

  /**
   * Visually activate a radio option and deactivate the other.
   * Works for both the onetime <button> and subscribe <div> containers.
   */
  activateRadio(activeType) {
    const radios = this.querySelectorAll("[data-sticky-radio]");
    radios.forEach((r) => {
      const active = r.dataset.stickyRadio === activeType;
      const dot = r.querySelector(".sticky-radio-dot");
      const check = r.querySelector("[data-sticky-check]");

      r.classList.toggle("border-blue-main", active);
      r.classList.toggle("bg-blue-main/5", active);
      r.classList.toggle("border-black/15", !active);

      if (dot) {
        dot.classList.toggle("border-blue-main", active);
        dot.classList.toggle("bg-blue-main", active);
        dot.classList.toggle("border-black/25", !active);
        dot.classList.toggle("bg-transparent", !active);
      }
      if (check) {
        check.classList.toggle("hidden", !active);
        check.classList.toggle("block", active);
      }
    });

    // Toggle frequency area
    const freqWrap = this.querySelector("[data-sticky-freq-wrap]");
    if (freqWrap) {
      if (activeType === "subscribe") {
        freqWrap.classList.remove("opacity-40", "pointer-events-none");
      } else {
        freqWrap.classList.add("opacity-40", "pointer-events-none");
        this.closeAllDropdowns();
      }
    }
  }

  /* ── Scroll show / hide ─────────────────────────── */

  setupScrollBehavior() {
    this.sticky = this.querySelector(".sticky-atc");
    const articleAuthor = document.querySelector(".article-author");
    const mainAtcBtn = document.querySelector(".atc-js");

    if (mainAtcBtn) {
      /* Product page: show after the ATC button scrolls out of view */
      this._atcVisible = true;
      const observer = new IntersectionObserver(
        (entries) => {
          this._atcVisible = entries[0].isIntersecting;
          this.updateStickyVisibility(articleAuthor);
        },
        { threshold: 0 }
      );
      observer.observe(mainAtcBtn);

      const onScroll = () => this.updateStickyVisibility(articleAuthor);
      window.addEventListener("scroll", onScroll, { passive: true });
    } else {
      /* Non-product pages: use scroll threshold */
      const getShowAfter = () => {
        const w = window.innerWidth;
        if (w < 640) return 600;
        if (w < 1024) return 800;
        return 300;
      };

      const onScroll = () => {
        const scrollY = window.scrollY;
        let shouldShow = scrollY > getShowAfter();
        let shouldHide = false;

        if (articleAuthor) {
          const authorBottom =
            articleAuthor.getBoundingClientRect().bottom + scrollY;
          shouldHide = scrollY + window.innerHeight > authorBottom;
        }

        if (shouldShow && !shouldHide) {
          this.sticky.classList.add("active");
        } else {
          this.sticky.classList.remove("active");
          this.closeAllDropdowns();
        }
      };

      window.addEventListener("scroll", onScroll);
      window.addEventListener("resize", onScroll);
    }
  }

  updateStickyVisibility(articleAuthor) {
    let shouldHide = false;
    if (articleAuthor) {
      const authorBottom =
        articleAuthor.getBoundingClientRect().bottom + window.scrollY;
      shouldHide = window.scrollY + window.innerHeight > authorBottom;
    }

    if (!this._atcVisible && !shouldHide) {
      this.sticky.classList.add("active");
    } else {
      this.sticky.classList.remove("active");
      this.closeAllDropdowns();
    }
  }

  /* ── Product sync (re-renders section from server) ── */

  buildRequestPath(productHandle, variantId, sellingPlanId) {
    if (!productHandle || !this.sectionId) return null;
    const params = new URLSearchParams();
    params.set("sections", this.sectionId);
    if (variantId) params.set("variant", variantId);
    if (sellingPlanId) params.set("selling_plan", sellingPlanId);
    return `/products/${productHandle}?${params.toString()}`;
  }

  async updateStickyATCHTML(productHandle, variantId, sellingPlanId) {
    if (!this.sectionId) return;

    if (!productHandle) {
      const mainProduct = this.getMainProductElement();
      if (!mainProduct) return;
      productHandle = mainProduct.dataset.productHandle;
      if (!productHandle) return;
      const vi = mainProduct.querySelector('input[name="id"]');
      const sp = mainProduct.querySelector('input[name="selling_plan"]');
      variantId = variantId || (vi ? vi.value : null);
      sellingPlanId = sellingPlanId || (sp ? sp.value : null);
    }

    const path = this.buildRequestPath(productHandle, variantId, sellingPlanId);
    if (!path) return;

    try {
      const res = await fetch(path);
      if (!res.ok) return;
      const json = await res.json();
      const html = json[this.sectionId];
      if (!html) return;
      const root = this.htmlToElement(html);
      if (!root) return;
      const next =
        root.querySelector("sticky-atc") ||
        root.querySelector(
          `[data-section-id="${this.sectionId}"] sticky-atc`
        );
      if (!next) return;
      next.dataset.sectionId = this.sectionId;
      this.replaceWith(next);
    } catch (err) {
      console.error("Error updating sticky ATC:", err);
    }
  }

  setupProductSync() {
    let timeout = null;
    window.addEventListener("main-product:selection-change", (event) => {
      if (timeout) clearTimeout(timeout);
      const { productHandle, variantId, sellingPlanId } = event.detail || {};

      const attempt = (n = 0) => {
        const mp = this.getMainProductElement();
        if (!mp) {
          if (n < 10) timeout = setTimeout(() => attempt(n + 1), 50);
          return;
        }
        const handle = productHandle || mp.dataset.productHandle;
        if (!handle) return;
        const vi = mp.querySelector('input[name="id"]');
        const sp = mp.querySelector('input[name="selling_plan"]');
        const dv = vi ? vi.value : null;
        const ds = sp ? sp.value : null;
        const fv = variantId !== undefined ? variantId : dv;
        const fs = sellingPlanId !== undefined ? sellingPlanId : ds;
        if (variantId && dv && variantId !== dv && n < 15) {
          timeout = setTimeout(() => attempt(n + 1), 50);
          return;
        }
        this.updateStickyATCHTML(handle, fv, fs);
      };

      timeout = setTimeout(() => attempt(), 150);
    });
  }

  /* ── Desktop radio buttons ──────────────────────── */

  setupDesktopRadios() {
    // One-time is a <button data-sticky-radio="onetime">
    const onetimeRadio = this.querySelector('[data-sticky-radio="onetime"]');
    // Subscribe is a <div data-sticky-radio="subscribe"> with inner <button data-sticky-sub-select>
    const subscribeContainer = this.querySelector(
      '[data-sticky-radio="subscribe"]'
    );
    const subscribeBtn = this.querySelector("[data-sticky-sub-select]");

    if (onetimeRadio) {
      onetimeRadio.addEventListener("click", () => {
        const price = onetimeRadio.dataset.price;
        this.activateRadio("onetime");
        this.updateSellingPlan("");
        this.updateDisplayPrice(price);

        // Sync mobile
        this.updateMobileLabel("onetime");
        this.activateMobileRadio("onetime");

        window.dispatchEvent(
          new CustomEvent("sticky-atc:purchase-change", {
            detail: { sellingPlanId: null, purchaseOption: "onetime" },
          })
        );
      });
    }

    if (subscribeBtn && subscribeContainer) {
      subscribeBtn.addEventListener("click", () => {
        const planId = subscribeContainer.dataset.planId || "";
        const price = subscribeContainer.dataset.price;
        this.activateRadio("subscribe");
        this.updateSellingPlan(planId);
        this.updateDisplayPrice(price);

        // Sync mobile
        const fl = this.querySelector("[data-sticky-freq-label]");
        this.updateMobileLabel("subscribe", fl ? fl.textContent.trim() : "");
        this.activateMobileRadio("subscribe");

        window.dispatchEvent(
          new CustomEvent("sticky-atc:purchase-change", {
            detail: { sellingPlanId: planId, purchaseOption: "subscribe" },
          })
        );
      });
    }
  }

  /* ── Desktop frequency dropdown ─────────────────── */

  setupDesktopFrequency() {
    const toggle = this.querySelector("[data-sticky-freq-toggle]");
    const options = this.querySelector("[data-sticky-freq-options]");
    if (!toggle || !options) return;

    const arrow = this.querySelector("[data-sticky-freq-arrow]");

    toggle.addEventListener("click", (e) => {
      e.stopPropagation();

      // Also select subscribe radio when opening frequency
      this.activateRadio("subscribe");

      const hidden = options.classList.contains("hidden");
      if (hidden) {
        options.classList.remove("hidden");
        if (arrow) arrow.style.transform = "rotate(180deg)";
      } else {
        options.classList.add("hidden");
        if (arrow) arrow.style.transform = "";
      }
    });

    options.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-freq-plan-id]");
      if (!btn) return;

      const planId = btn.dataset.freqPlanId;
      const price = btn.dataset.freqPrice;
      const label = btn.dataset.freqLabel; // e.g. "30 Days"

      // Update frequency label
      const fl = this.querySelector("[data-sticky-freq-label]");
      if (fl) fl.textContent = label;

      // Update subscribe container data
      const subContainer = this.querySelector(
        '[data-sticky-radio="subscribe"]'
      );
      if (subContainer) {
        subContainer.dataset.planId = planId;
        subContainer.dataset.price = price;
        const sp = subContainer.querySelector("[data-sticky-sub-price]");
        if (sp) sp.textContent = price;
      }

      this.updateSellingPlan(planId);
      this.updateDisplayPrice(price);

      // Sync mobile
      this.updateMobileLabel("subscribe", label);

      options.classList.add("hidden");
      if (arrow) arrow.style.transform = "";

      window.dispatchEvent(
        new CustomEvent("sticky-atc:purchase-change", {
          detail: { sellingPlanId: planId, purchaseOption: "subscribe" },
        })
      );
    });
  }

  /* ── Mobile label helper ───────────────────────── */

  updateMobileLabel(type, freqLabel) {
    const ml = this.querySelector("[data-sticky-mobile-label]");
    if (!ml) return;
    if (type === "subscribe" && freqLabel) {
      ml.innerHTML =
        '<span class="text-sm font-body-medium whitespace-nowrap text-blue-main">Subscribe & Save</span>' +
        '<span class="text-[10px] text-blue-main/50 whitespace-nowrap">' + freqLabel + '</span>';
    } else {
      ml.innerHTML =
        '<span class="text-sm font-body-medium whitespace-nowrap text-blue-main">One-time Purchase</span>';
    }
  }

  /* ── Mobile radio helper ─────────────────────── */

  activateMobileRadio(activeType) {
    this.querySelectorAll("[data-mobile-option]").forEach((opt) => {
      const active = opt.dataset.mobileOption === activeType;
      const dot = opt.querySelector(".sticky-mobile-radio");
      const check = dot ? dot.querySelector("svg") : null;
      if (dot) {
        dot.classList.toggle("border-blue-main", active);
        dot.classList.toggle("bg-blue-main", active);
        dot.classList.toggle("border-black/25", !active);
        dot.classList.toggle("bg-transparent", !active);
      }
      if (check) {
        check.classList.toggle("hidden", !active);
        check.classList.toggle("block", active);
      }
    });
  }

  /* ── Mobile expanded panel ───────────────────── */

  setupMobileDropdown() {
    const toggle = this.querySelector("[data-sticky-mobile-toggle]");
    const panel = this.querySelector("[data-sticky-mobile-options]");
    if (!toggle || !panel) return;

    const arrow = this.querySelector("[data-sticky-mobile-arrow]");
    const freqSelect = this.querySelector("[data-sticky-mobile-freq]");

    // Toggle expanded panel
    toggle.addEventListener("click", (e) => {
      e.stopPropagation();
      const hidden = panel.classList.contains("hidden");
      if (hidden) {
        panel.classList.remove("hidden");
        if (arrow) arrow.style.transform = "rotate(180deg)";
        toggle.setAttribute("aria-expanded", "true");
      } else {
        panel.classList.add("hidden");
        if (arrow) arrow.style.transform = "";
        toggle.setAttribute("aria-expanded", "false");
      }
    });

    // Option selection (subscribe or onetime)
    panel.querySelectorAll("[data-mobile-option]").forEach((opt) => {
      opt.addEventListener("click", (e) => {
        // Don't close if clicking the frequency select
        if (e.target.closest("[data-sticky-mobile-freq]")) return;

        const type = opt.dataset.mobileOption;
        const planId = opt.dataset.mobilePlan;
        const price = opt.dataset.mobilePrice;

        this.activateMobileRadio(type);
        this.updateSellingPlan(planId);
        this.updateDisplayPrice(price);
        this.activateRadio(type);

        // Update mobile label
        if (type === "subscribe") {
          const freqEl = this.querySelector("[data-sticky-mobile-freq]");
          const freqLabel = freqEl ? freqEl.options[freqEl.selectedIndex].text : "";
          this.updateMobileLabel("subscribe", freqLabel);
        } else {
          this.updateMobileLabel("onetime");
        }

        // Sync desktop subscribe data
        if (type === "subscribe") {
          const subContainer = this.querySelector('[data-sticky-radio="subscribe"]');
          if (subContainer) {
            subContainer.dataset.planId = planId;
            subContainer.dataset.price = price;
            const sp = subContainer.querySelector("[data-sticky-sub-price]");
            if (sp) sp.textContent = price;
          }
          const fl = this.querySelector("[data-sticky-freq-label]");
          const freqEl = this.querySelector("[data-sticky-mobile-freq]");
          if (fl && freqEl) fl.textContent = freqEl.options[freqEl.selectedIndex].text;
        }

        // Close panel
        panel.classList.add("hidden");
        if (arrow) arrow.style.transform = "";
        toggle.setAttribute("aria-expanded", "false");

        window.dispatchEvent(
          new CustomEvent("sticky-atc:purchase-change", {
            detail: { sellingPlanId: planId || null, purchaseOption: type },
          })
        );
      });
    });

    // Frequency select change (within subscribe option)
    if (freqSelect) {
      freqSelect.addEventListener("click", (e) => e.stopPropagation());
      freqSelect.addEventListener("change", (e) => {
        const selected = freqSelect.options[freqSelect.selectedIndex];
        const planId = freqSelect.value;
        const price = selected.dataset.price;
        const label = selected.text;

        // Update subscribe option data
        const subOpt = panel.querySelector('[data-mobile-option="subscribe"]');
        if (subOpt) {
          subOpt.dataset.mobilePlan = planId;
          subOpt.dataset.mobilePrice = price;
        }

        // Update sub price in panel
        const subPrice = panel.querySelector("[data-mobile-sub-price]");
        if (subPrice) subPrice.textContent = price;

        // If subscribe is active, update everything
        const subDot = subOpt ? subOpt.querySelector(".sticky-mobile-radio") : null;
        const isSubActive = subDot && subDot.classList.contains("bg-blue-main");
        if (isSubActive) {
          this.updateSellingPlan(planId);
          this.updateDisplayPrice(price);
          this.updateMobileLabel("subscribe", label);

          // Sync desktop
          const subContainer = this.querySelector('[data-sticky-radio="subscribe"]');
          if (subContainer) {
            subContainer.dataset.planId = planId;
            subContainer.dataset.price = price;
            const sp = subContainer.querySelector("[data-sticky-sub-price]");
            if (sp) sp.textContent = price;
          }
          const fl = this.querySelector("[data-sticky-freq-label]");
          if (fl) fl.textContent = label;

          window.dispatchEvent(
            new CustomEvent("sticky-atc:purchase-change", {
              detail: { sellingPlanId: planId, purchaseOption: "subscribe" },
            })
          );
        }
      });
    }

    // Close panel on outside click
    this._outsideClick = (event) => {
      if (!this.contains(event.target)) {
        this.closeAllDropdowns();
      }
    };
    document.addEventListener("click", this._outsideClick);
  }
}

customElements.define("sticky-atc", StickyATC);
